import { useEffect, useState } from 'react';

import { Button } from '../../../../../components/Button';
import { Modal } from '../../../../../components/Modal';
import { Select } from '../../../../../components/inputs/Select';
import { ApiError } from '../../../../../lib/apiError';
import { focusValidationError } from '../../../../../lib/validationFocus';
import { personnelApi } from '../../personnelApi';
import type { LinkableAccount, PersonnelDetail } from '../../personnelTypes';
import { toUpdateRequest } from '../../utils/toUpdateRequest';

/**
 * Which account this employee signs in as.
 *
 * **It edits the LINK, not the account.** `userId` is a column on the personnel record, so
 * changing it is this module's own write under `personnel.update`. The account's own fields —
 * its alias, its email, its role, its lockout — belong to the users module and are corrected
 * on the user's own profile, which the card links out to. That split is the rule about a card
 * over another module's record, and it is the reason this modal has one control in it.
 *
 * **Creating a new account is not here.** The create wizard mints one because that is where a
 * new employee's credential is issued and shown once; the update endpoint ignores an account
 * block entirely, so offering it here would be a control that silently did nothing.
 */
export function EditAccountLinkModal({
  open,
  record,
  onClose,
  onSaved,
}: {
  open: boolean;
  record: PersonnelDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [userId, setUserId] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<LinkableAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();

    setUserId(record.userId ?? null);
    setConfirmed(false);
    setError(null);
    setLoading(true);

    personnelApi
      .linkableAccounts(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setAccounts(next);
      })
      .catch(() => {
        // Not a field's fault, and the reader can still unlink — the list is what is missing.
        if (!controller.signal.aborted) setAccounts([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open, record.userId]);

  if (!open) return null;

  /*
    The feed lists accounts with no employee yet, so the one already linked is NOT in it. It is
    injected from the record — with its alias, not just its id — or the picker would open on a
    placeholder and read as though nothing were linked. Same fix the org chart's locked parent
    field needs, for the same reason.
  */
  const options = [
    ...(record.userId ? [{ value: record.userId, label: record.userAlias ?? record.userId }] : []),
    ...accounts
      .filter((account) => account.id !== record.userId)
      .map((account) => ({ value: account.id, label: account.alias, sublabel: account.email })),
  ];

  const changed = (userId ?? null) !== (record.userId ?? null);
  const canSave = changed && confirmed && !saving && !loading;
  const unlinking = record.userId !== null && record.userId !== undefined && userId === null;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await personnelApi.update(record.id, { ...toUpdateRequest(record), userId });
      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not change the account.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'personnel-account' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="User account"
      size="lg"
      id="personnel-account-modal"
      /*
        Picks one of the accounts the server already said are linkable — a real row, which is
        what the rule asks for, and one it has already confirmed is free to link.

        Disabled with none available: a generator that left the field null would produce a
        form that still refuses to save, which reads as the button being broken.
      */
      autogenerate={{
        onGenerate: () => {
          if (accounts.length > 0) {
            setUserId(accounts[Math.floor(Math.random() * accounts.length)].id);
          }
        },
        disabled: saving || accounts.length === 0,
      }}
      confirm={{
        id: 'personnel-account-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="personnel-account-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            id="personnel-account-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          The id resolves what the server names: `userId` kebabs to `user-id` under this
          prefix, so a rejection lands on the control rather than on nothing.
        */}
        <Select
          id="personnel-account-user-id"
          label="Account"
          description="Accounts with no employee yet, plus the one already linked. Clearing it unlinks."
          value={userId}
          onChange={setUserId}
          options={options}
          loading={loading}
          error={error?.fieldError('userId')}
          disabled={saving}
        />

        {!loading && options.length === 0 ? (
          <p className="text-xs text-slate-500">
            No account is free to link. Create one from the Users module first, or from the
            personnel form when adding an employee.
          </p>
        ) : null}

        {/* The consequence, on the form and before the tick — never appended to it. */}
        {unlinking ? (
          <p className="text-xs text-slate-500">
            Saving unlinks the account. The account itself is untouched and can sign in as before;
            it simply stops being attached to this employee.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
